/**
 * Flotte automobile — VEOS endpoints of the fleet list and of the vehicle
 * detail (fiches 22756 / 22809 / 22811 / 22768 / 22776 / 22778, contract §6).
 *
 * Every call goes through origami's ERP proxy ({@link fetchErpRows} /
 * {@link fetchErpRecord}); parameters are cased exactly as in the fiches
 * (VEOS is case-sensitive: `ID_NPOL` / `ID_VEH` on FIC_VEH and TAB_DOCS_VEH,
 * `id_npol` / `id_veh` on FIC_POL_VEH, TAB_GARANTIES_VEH and TAB_SIN_VEH).
 * `SES` and `perPage` are added server-side.
 *
 * Raw rows are typed as VEOS returns them and mapped to view models:
 * texts trimmed (`" EN COURS"` → `"EN COURS"`, `" "` → `null`), amounts
 * coerced to numbers, `null` kept as `null` (the cards render nothing for it).
 */

import type { ClaimTableRow } from '../components/shared/claimsTableConfig';
import type { DocumentItem } from '../components/shared/documentItems';
import type { GuaranteeTableRow } from '../components/shared/guaranteesTableConfig';
import { formatVeosDate, toFiniteNumber, type VeosScalar } from '../utils/format';
import { fetchErpRecord, fetchErpRows } from './erpProxy';
import { buildFilterParams } from './listFilters';

// ---------------------------------------------------------------------------
// Endpoint slugs (contract §6)
// ---------------------------------------------------------------------------

/** `WS2_EA_TAB_GEN_VEH` — fleet list (fiche 22756). */
export const TAB_GEN_VEH_ENDPOINT = 'boby-ws2-ea-tab-gen-veh';
/** `WS2_EA_FIC_VEH` — vehicle record (fiche 22809). */
export const FIC_VEH_ENDPOINT = 'boby-ws2-ea-fic-veh';
/** `WS2_EA_FIC_POL_VEH` — contract of the vehicle (fiche 22811). */
export const FIC_POL_VEH_ENDPOINT = 'boby-ws2-ea-fic-pol-veh';
/** `WS2_EA_TAB_GARANTIES_VEH` — guarantees of the vehicle (fiche 22768). */
export const TAB_GARANTIES_VEH_ENDPOINT = 'boby-ws2-ea-tab-garanties-veh';
/** `WS2_EA_TAB_SIN_VEH` — claims of the vehicle (fiche 22776). */
export const TAB_SIN_VEH_ENDPOINT = 'boby-ws2-ea-tab-sin-veh';
/** `WS2_EA_TAB_DOCS_VEH` — documents of the vehicle (fiche 22778). */
export const TAB_DOCS_VEH_ENDPOINT = 'boby-ws2-ea-tab-docs-veh';

// ---------------------------------------------------------------------------
// Raw VEOS rows (only the fields the screens read are typed)
// ---------------------------------------------------------------------------

/** One `WS2_EA_TAB_GEN_VEH` row. */
export interface FleetVehicleApiRow {
  ID_NPOL: number | string | null;
  ID_VEH: number | string | null;
  IMMAT_VEH: string | null;
  MARQ_MODL_VEH: string | null;
  DTENT_POL: string | null;
  DTSOR_POL: string | null;
  VEH_SITUATION: string | null;
}

/** The `WS2_EA_FIC_VEH` record. */
export interface VehicleApiRecord {
  ID_NPOL: number | string | null;
  ID_VEH: number | string | null;
  IMMAT_VEH: string | null;
  MARQ_VEH: string | null;
  MODL_VEH: string | null;
  DATM_VEH: string | null;
  CONDUC_VEH: string | null;
  DTENT_POL: string | null;
  VEH_SITUATION: string | null;
  CATV_VEH: string | null;
}

/** The `WS2_EA_FIC_POL_VEH` record. */
export interface VehicleContractApiRecord {
  NUM_POL: string | null;
  LIB_PROD: string | null;
  NOM_CIE: string | null;
  NOM2_PER_ASSURE: string | null;
  DTEFF_POL: string | null;
  DTFIN_POL: string | null;
  TTC_POL: number | string | null;
  LIB_FRAC: string | null;
}

/** One `WS2_EA_TAB_GARANTIES_VEH` row. */
export interface VehicleGuaranteeApiRow {
  LIB_GAR: string | null;
  CAP_GARP: number | string | null;
  FRA_GARP: number | string | null;
}

/** One `WS2_EA_TAB_SIN_VEH` row. */
export interface VehicleClaimApiRow {
  ID_SIN: number | string | null;
  NCBT_SIN: string | null;
  LIB_CIRC: string | null;
  /** Unix timestamp in ms. */
  DATS_SIN: number | string | null;
  COUT_TOTAL_ASSUREUR: number | string | null;
}

/** One `WS2_EA_TAB_DOCS_VEH` row. */
export interface VehicleDocumentApiRow {
  ID_ADOC: number | string | null;
  LIB_ADOC: string | null;
  /** Unix timestamp in ms. */
  DAT_ADOC: number | string | null;
  LIB_DS: string | null;
}

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

/** One vehicle of the fleet list. */
export interface FleetVehicle {
  /** Stable row id (`ID_NPOL-ID_VEH`). */
  id: string;
  /** Contract id (`ID_NPOL`) — first key of the vehicle detail route. */
  idNpol: string | null;
  /** Vehicle id (`ID_VEH`) — second key of the vehicle detail route. */
  idVeh: string | null;
  /** `IMMAT_VEH`. */
  immatriculation: string | null;
  /** `MARQ_MODL_VEH` (never rebuilt from MARQ_VEH / MODL_VEH — fiche 22756). */
  marqueModele: string | null;
  /** `DTENT_POL`, already `JJ/MM/AAAA`. */
  dateEntree: string | null;
  /** `DTSOR_POL`, already `JJ/MM/AAAA`; `null` ⇒ empty cell. */
  dateSortie: string | null;
  /** `VEH_SITUATION`, trimmed (« EN COURS » / « SORTI »). */
  statut: string | null;
}

/** The vehicle record (« Détails » tab + page header + rule context). */
export interface VehicleDetails {
  idNpol: string | null;
  idVeh: string | null;
  /** `IMMAT_VEH`. */
  immatriculation: string | null;
  /** `MARQ_VEH`. */
  marque: string | null;
  /** `MODL_VEH`. */
  modele: string | null;
  /** `DATM_VEH`, already `JJ/MM/AAAA`. */
  miseEnCirculation: string | null;
  /** `CONDUC_VEH` (VEOS returns `" "` when unknown ⇒ `null`). */
  conducteur: string | null;
  /** `DTENT_POL`, already `JJ/MM/AAAA`. */
  dateEntree: string | null;
  /** `VEH_SITUATION`, trimmed (VEOS returns `" EN COURS"`). */
  statut: string | null;
  /** `CATV_VEH` (rule context only). */
  categorie: string | null;
}

/** The contract of the vehicle (« Contrat » tab). */
export interface VehicleContract {
  /** `NUM_POL`. */
  numero: string | null;
  /** `LIB_PROD`. */
  produit: string | null;
  /** `NOM_CIE`. */
  assureur: string | null;
  /** `NOM2_PER_ASSURE` (not `NOM2_PER` — fiche 22811). */
  assure: string | null;
  /** `DTEFF_POL`, already `JJ/MM/AAAA`. */
  dateEffet: string | null;
  /** `DTFIN_POL`, already `JJ/MM/AAAA`; `null` ⇒ empty value. */
  dateEcheance: string | null;
  /** `TTC_POL` in euros. */
  primeAnnuelle: number | null;
  /** `LIB_FRAC`. */
  fractionnement: string | null;
}

/** A claim of the vehicle: the shared claims-table row + its `ID_SIN`. */
export interface VehicleClaim extends ClaimTableRow {
  /** `ID_SIN` — key of the claim detail route; `null` ⇒ row not clickable. */
  idSin: string | null;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/** A VEOS text or id: trimmed string; `null` / blank ⇒ `null`. */
function textOrNull(value: VeosScalar): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

/** A VEOS date (ms timestamp or `JJ/MM/AAAA` string) as `JJ/MM/AAAA`, else `null`. */
function dateOrNull(value: VeosScalar): string | null {
  const text = formatVeosDate(value);
  return text === '' ? null : text;
}

export function toFleetVehicle(row: FleetVehicleApiRow, index: number): FleetVehicle {
  const idNpol = textOrNull(row.ID_NPOL);
  const idVeh = textOrNull(row.ID_VEH);
  return {
    id: idNpol !== null && idVeh !== null ? `${idNpol}-${idVeh}` : `vehicule-${String(index)}`,
    idNpol,
    idVeh,
    immatriculation: textOrNull(row.IMMAT_VEH),
    marqueModele: textOrNull(row.MARQ_MODL_VEH),
    dateEntree: textOrNull(row.DTENT_POL),
    dateSortie: textOrNull(row.DTSOR_POL),
    statut: textOrNull(row.VEH_SITUATION),
  };
}

export function toVehicleDetails(row: VehicleApiRecord): VehicleDetails {
  return {
    idNpol: textOrNull(row.ID_NPOL),
    idVeh: textOrNull(row.ID_VEH),
    immatriculation: textOrNull(row.IMMAT_VEH),
    marque: textOrNull(row.MARQ_VEH),
    modele: textOrNull(row.MODL_VEH),
    miseEnCirculation: textOrNull(row.DATM_VEH),
    conducteur: textOrNull(row.CONDUC_VEH),
    dateEntree: textOrNull(row.DTENT_POL),
    statut: textOrNull(row.VEH_SITUATION),
    categorie: textOrNull(row.CATV_VEH),
  };
}

export function toVehicleContract(row: VehicleContractApiRecord): VehicleContract {
  return {
    numero: textOrNull(row.NUM_POL),
    produit: textOrNull(row.LIB_PROD),
    assureur: textOrNull(row.NOM_CIE),
    assure: textOrNull(row.NOM2_PER_ASSURE),
    dateEffet: textOrNull(row.DTEFF_POL),
    dateEcheance: textOrNull(row.DTFIN_POL),
    primeAnnuelle: toFiniteNumber(row.TTC_POL),
    fractionnement: textOrNull(row.LIB_FRAC),
  };
}

export function toVehicleGuarantee(row: VehicleGuaranteeApiRow, index: number): GuaranteeTableRow {
  return {
    id: `garantie-${String(index)}`,
    libelle: textOrNull(row.LIB_GAR),
    plafond: toFiniteNumber(row.CAP_GARP),
    franchise: toFiniteNumber(row.FRA_GARP),
  };
}

export function toVehicleClaim(row: VehicleClaimApiRow, index: number): VehicleClaim {
  const idSin = textOrNull(row.ID_SIN);
  const date = row.DATS_SIN;
  return {
    id: idSin ?? `sinistre-${String(index)}`,
    idSin,
    reference: textOrNull(row.NCBT_SIN),
    circonstances: textOrNull(row.LIB_CIRC),
    // ms timestamp kept raw (the shared date renderer formats it and the
    // column sorts chronologically); a string is shown as returned.
    dateSurvenance: typeof date === 'number' ? date : textOrNull(date),
    coutAssureur: toFiniteNumber(row.COUT_TOTAL_ASSUREUR),
  };
}

export function toVehicleDocument(row: VehicleDocumentApiRow, index: number): DocumentItem {
  return {
    id: textOrNull(row.ID_ADOC) ?? `document-${String(index)}`,
    categorie: textOrNull(row.LIB_DS),
    nom: textOrNull(row.LIB_ADOC),
    date: dateOrNull(row.DAT_ADOC),
  };
}

// ---------------------------------------------------------------------------
// List filters (fiche 22987)
// ---------------------------------------------------------------------------

/** Server-side filter of the fleet list: omitted from the request when blank. */
export interface FleetListFilters {
  /** Free-text search (immat, marque, modèle…); blank ⇒ not sent. */
  query?: string | null;
}

/** Request params of `WS2_EA_TAB_GEN_VEH`: `query` only when non-blank (trimmed). */
export function buildFleetListParams(filters: FleetListFilters = {}): Record<string, string> {
  return buildFilterParams({ query: filters.query });
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const fleetService = {
  /**
   * The fleet, in API order, filtered server-side by `query` (the « Rechercher
   * par immat, marque, modèle… » text — fiche 22987), sent only when non-blank.
   */
  async fetchVehicles(
    registrySlug: string | null | undefined,
    filters: FleetListFilters = {},
  ): Promise<FleetVehicle[]> {
    const rows = await fetchErpRows<FleetVehicleApiRow>(
      registrySlug,
      TAB_GEN_VEH_ENDPOINT,
      buildFleetListParams(filters),
    );
    return rows.map(toFleetVehicle);
  },

  /** The vehicle record, or `null` when VEOS returned none. */
  async fetchVehicle(
    registrySlug: string | null | undefined,
    idNpol: string,
    idVeh: string,
  ): Promise<VehicleDetails | null> {
    const row = await fetchErpRecord<VehicleApiRecord>(registrySlug, FIC_VEH_ENDPOINT, {
      ID_NPOL: idNpol,
      ID_VEH: idVeh,
    });
    return row === null ? null : toVehicleDetails(row);
  },

  /** The contract of the vehicle, or `null` when VEOS returned none. */
  async fetchVehicleContract(
    registrySlug: string | null | undefined,
    idNpol: string,
    idVeh: string,
  ): Promise<VehicleContract | null> {
    const row = await fetchErpRecord<VehicleContractApiRecord>(
      registrySlug,
      FIC_POL_VEH_ENDPOINT,
      { id_npol: idNpol, id_veh: idVeh },
    );
    return row === null ? null : toVehicleContract(row);
  },

  /** The guarantees of the vehicle, in API order. */
  async fetchVehicleGuarantees(
    registrySlug: string | null | undefined,
    idNpol: string,
    idVeh: string,
  ): Promise<GuaranteeTableRow[]> {
    const rows = await fetchErpRows<VehicleGuaranteeApiRow>(
      registrySlug,
      TAB_GARANTIES_VEH_ENDPOINT,
      { id_npol: idNpol, id_veh: idVeh },
    );
    return rows.map(toVehicleGuarantee);
  },

  /** The claims of the vehicle, in API order. */
  async fetchVehicleClaims(
    registrySlug: string | null | undefined,
    idNpol: string,
    idVeh: string,
  ): Promise<VehicleClaim[]> {
    const rows = await fetchErpRows<VehicleClaimApiRow>(registrySlug, TAB_SIN_VEH_ENDPOINT, {
      id_npol: idNpol,
      id_veh: idVeh,
    });
    return rows.map(toVehicleClaim);
  },

  /** The documents of the vehicle, in API order (grouped by the card). */
  async fetchVehicleDocuments(
    registrySlug: string | null | undefined,
    idNpol: string,
    idVeh: string,
  ): Promise<DocumentItem[]> {
    const rows = await fetchErpRows<VehicleDocumentApiRow>(registrySlug, TAB_DOCS_VEH_ENDPOINT, {
      ID_NPOL: idNpol,
      ID_VEH: idVeh,
    });
    return rows.map(toVehicleDocument);
  },
};
